/** @jsx jsx */
import React, {
  FC,
  AnchorHTMLAttributes,
  HTMLProps,
  MouseEvent,
  ReactNode,
  RefAttributes,
  SyntheticEvent,
  ButtonHTMLAttributes,
  useState,
  useEffect,
  ReactElement
} from 'react';

import { css, jsx, withTheme } from '@emotion/react';
import * as CSS from 'csstype';
import { navigate } from '@reach/router';
import {
  Icon,
  IconType
} from 'lucid-components-react';
import { ThemeProps } from 'lucid-components-react/dist/theme/theme';
import { withPrefix } from 'gatsby';

import StyledGatsbyLink from './styled-gatsby-link';
import {
  MarketingPageElementLocation,
  MarketingPageElementType
} from '../../../src/types/beaconEventTypes';
import usePathPrefix from '../../hooks/use-path-prefix';
import useSiteMetadata from '../../hooks/use-site-metadata';
import { sendBeaconEvent } from '../../utils/events/send-beacon-event';
import withCookieSyncParams from '../../utils/withCookieSyncParams';

const DEFAULT_BLOCKLIST = ['documents', 'user', 'users', 'blog'];
export type UnderlineType = 'none' | 'hover' | 'always';
export type ButtonSize = 'small' | 'regular' | 'large';
export enum LinkVariant {
  default = 'default',
  primary = 'primary',
  secondary = 'secondary'
}

enum Products {
  chart = 'chart',
  spark = 'spark',
  co = 'co',
  press = 'press',
  edu = 'edu'
}

interface LinkProps {
  active?: boolean;
  activeClassName?: string;
  beaconEvent?: {
    name: string;
    elementLocation: MarketingPageElementLocation;
    elementDetail: string;
    elementType: MarketingPageElementType;
  } | null;
  blocklist?: string[];
  checkHistory?: boolean;
  externalAlwaysNewTab?: boolean;
  excludeFromGatsby?: boolean;
  hasArrow?: boolean;
  partiallyActive?: boolean;
  product?: Products;
  newTab?: boolean;
  rel?: string;
  theme?: ThemeProps['theme'];
}

interface LinkButtonBaseProps {
  block?: boolean;
  buttonSize?: ButtonSize;
  disabled?: boolean;
  inverse?: boolean;
  primary?: boolean;
  secondary?: boolean;
  underline?: UnderlineType;
}

interface LinkBaseProps {
  asButton?: boolean;
  displayAsButton?: boolean;
  href?: string;
  ref?: HTMLDivElement;
  to?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  variant?: LinkVariant;
}

interface ButtonBaseProps {
  active?: boolean;
  children?: ReactNode;
  className?: string;
  hover?: boolean;
  id?: string;
  kmTrigger?: string;
  name?: string;
  onClick?: ((evt: SyntheticEvent<Element, Event>) => void) | (() => void);
  onHover?: ((evt: SyntheticEvent<Element, Event>) => void) | (() => void);
  onFocus?: ((evt: SyntheticEvent<Element, Event>) => void) | (() => void);
  onMouseOver?: ((evt: SyntheticEvent<Element, Event>) => void) | (() => void);
}

interface LinkBaseStyles {
  block?: boolean;
  hasArrow?: boolean;
  inverse?: boolean;
  isActive?: boolean;
  theme?: ThemeProps['theme'],
  underlineLink?: string;
  underlineLinkOnHover?: string;
  variant?: LinkVariant;
}

interface ButtonBaseStyles {
  block?: boolean;
  buttonSize?: ButtonSize;
  disabled?: boolean;
  isActive?: boolean;
  theme?: ThemeProps['theme'],
  variant?: LinkVariant;
}

export type LinkInterface = ThemeProps &
  HTMLProps<HTMLAnchorElement> &
  AnchorHTMLAttributes<HTMLAnchorElement> &
  ButtonHTMLAttributes<HTMLButtonElement> &
  RefAttributes<HTMLAnchorElement> &
  LinkProps &
  LinkButtonBaseProps &
  LinkBaseProps &
  ButtonBaseProps;

const linkBaseStyles = ({
  block,
  hasArrow,
  inverse,
  isActive,
  theme,
  underlineLink,
  underlineLinkOnHover,
  variant
}: LinkBaseStyles) => {
  const linkType = inverse ? 'inverse' : 'default';
  const { color, hover }: {
    color: CSS.Properties['color'],
    hover: CSS.Properties['color']
  } = theme?.links[variant!][linkType];

  const linkBaseHoverCss = {
    label: 'linkBaseHover',
    color: hover,
    textDecoration: underlineLinkOnHover,
    '&:visited': {
      color: hover
    }
  };

  const linkBaseCss = {
    label: 'linkBase',
    color,
    display: block ? 'block' : 'inline-block',
    border: 'none',
    fontSize: 'inherit',
    textAlign: 'left',
    textDecoration: underlineLink,
    cursor: 'pointer',
    '&:visited': {
      color
    },
    '&:hover, &:focus': linkBaseHoverCss,
    // 'a&:not([href])': {
    //   color: disabled,
    //   cursor: 'not-allowed'
    // }
  };

  const linkHasArrowHoverCss = {
    '&:hover, &:focus': {
      '.hasArrow': {
        transform: 'translateX(0.15rem)'
      }
    }
  };

  const isActiveCSS = [linkBaseCss, linkBaseHoverCss, hasArrow && linkHasArrowHoverCss];
  const baseCSS = [linkBaseCss, hasArrow && linkHasArrowHoverCss];

  return isActive ? isActiveCSS : baseCSS;
};

const buttonBaseStyles = ({
  block,
  buttonSize,
  disabled,
  isActive,
  theme,
  variant
}: ButtonBaseStyles) => {
  const { padding } = theme?.buttons.sizes[buttonSize!];
  const isPrimary = variant === LinkVariant.primary;
  const isSecondary = variant === LinkVariant.secondary;

  const buttonBaseHoverCss = (key: 'primary' | 'secondary' | 'default') => ({
    '&:hover, &:focus': {
      label: 'buttonBaseHover',
      color: key === 'default' ? theme?.colors.black : theme?.buttons[key].hoverColor,
      backgroundColor: key === 'default' ? theme?.colors.grey : theme?.buttons[key].hoverBackgroundColor,
      border: key === 'default' ? theme?.buttons.border : theme?.buttons[key].hoverBorder,
      boxShadow: key === 'default' ? theme?.buttons.hoverBoxShadow : theme?.buttons.hoverBoxShadow,
      cursor: 'pointer',
      textDecoration: 'none'
    }
  });

  const buttonDisabledCss = [
    {
      color: theme?.buttons.disabledColor,
      backgroundColor: theme?.buttons.disabledBackgroundColor,
      border: theme?.buttons.disabledBorder,
      boxShadow: theme?.buttons.disabledBoxShadow,
      '&:visited': {
        color: theme?.buttons.disabledColor
      },
    }
  ];

  const defaultButtonCss = [
    {
      color: theme?.colors.black,
      backgroundColor: theme?.colors.white,
      border: theme?.buttons.border,
      boxShadow: theme?.buttons.boxShadow,
      '&:visited': {
        color: theme?.colors.white
      },
      transition: 'transform 0.15s ease-out'
    },
    buttonBaseHoverCss('default')
  ];

  const primaryButtonCss = [
    {
      color: theme?.colors.white,
      backgroundColor: theme?.buttons.primary.backgroundColor,
      border: theme?.buttons.primary.border,
      boxShadow: theme?.buttons.boxShadow,
      '&:visited': {
        color: theme?.colors.white
      },
      transition: 'transform 0.15s ease-out'
    },
    buttonBaseHoverCss('primary')
  ];

  const secondaryButtonCss = [
    {
      color: theme?.buttons.secondary.color,
      backgroundColor: theme?.buttons.secondary.backgroundColor,
      border: theme?.buttons.secondary.border,
      boxShadow: theme?.buttons.boxShadow,
      '&:visited': {
        color: theme?.buttons.secondary.color
      },
      transition: 'transform 0.15s ease-out'
    },
    buttonBaseHoverCss('secondary')
  ];

  const buttonVariantCss = isPrimary
    ? primaryButtonCss
    : isSecondary
      ? secondaryButtonCss
      : disabled
        ? buttonDisabledCss
        : defaultButtonCss;

  const buttonBaseCss = [
    {
      label: 'buttonBase',
      display: block ? 'block' : 'inline-block',
      width: block ? '100%' : 'auto',
      textAlign: 'center' as CSS.Property.TextAlign,
      padding,
      fontSize: theme?.buttons.fontSize || 'inherit',
      fontWeight: 'bold' as CSS.Property.FontWeight,
      lineHeight: theme?.buttons.lineHeight,
      borderRadius: theme?.radii.button,
      textDecoration: 'none'
    },
    buttonBaseHoverCss(variant!)
  ];

  const isActiveCSS = [
    ...buttonBaseCss,
    ...buttonVariantCss,
    buttonBaseHoverCss(variant!)
  ];
  const baseCSS = [buttonBaseCss, buttonVariantCss];

  return isActive ? isActiveCSS : baseCSS;
};

// Since DOM elements <a> cannot receive activeClassName
// and partiallyActive, destructure the prop here and
// pass it only to GatsbyLink
const LinkBase: FC<LinkInterface> = ({
  active,
  activeClassName,
  asButton,
  beaconEvent,
  buttonSize = 'regular',
  block,
  blocklist = [],
  checkHistory,
  children,
  className,
  defaultValue,
  disabled,
  displayAsButton,
  externalAlwaysNewTab,
  excludeFromGatsby,
  href,
  inverse,
  newTab,
  onClick,
  partiallyActive,
  product,
  primary,
  hasArrow = primary,
  ref,
  rel,
  secondary,
  theme,
  to,
  type,
  underline,
  variant = LinkVariant.default,
  ...rest
}): ReactElement => {
  const { siteDomain, product: siteProduct } = useSiteMetadata();
  const pathPrefix = usePathPrefix();
  const hrefToValue: string | undefined = (to || href) || '';
  const [hrefTo, setHrefTo] = useState(hrefToValue);
  // FIXME: clean up product checks
  const productName = !product ? siteProduct : product;
  const isLucidchart = productName === Products.chart;
  const isLucidspark = productName === Products.spark;
  const isLucidco = productName === Products.co;
  const isLucidpress = productName === Products.press;
  const isLucidedu = productName === Products.edu;

  const mergeBlocklist = (isLucidchart || isLucidspark) && !blocklist.length
    ? [...DEFAULT_BLOCKLIST, ...blocklist]
    : DEFAULT_BLOCKLIST;
  const isActive = active;
  const underlineLink = underline === 'always' ? 'underline' : 'none';
  const underlineLinkOnHover =
    underline === 'hover' || underline === 'always' ? 'underline' : 'none';
  const linkVariant = primary
    ? LinkVariant.primary
    : secondary
      ? LinkVariant.secondary
      : variant;

  const linkStyles = linkBaseStyles({
    block,
    hasArrow,
    inverse,
    isActive,
    theme,
    underlineLink,
    underlineLinkOnHover,
    variant: linkVariant
  });

  const buttonStyles = buttonBaseStyles({
    block,
    disabled,
    isActive,
    buttonSize,
    theme,
    variant: linkVariant
  });

  useEffect(() => {
    const hrefToLink = withCookieSyncParams(hrefTo);

    return setHrefTo(hrefToLink);
  }, [hrefTo]);

  try {
    if (asButton && (!onClick && !type)) {
      throw `You must provide either an 'onClick' function or a 'type' when Link prop 'asButton' is true for ${hrefTo}.`;
    }
  } catch (error) {
    throw new Error(error);
  }

  try {
    if (displayAsButton && ((!hrefTo) && !onClick)) {
      throw "You must provide either a 'href', 'to', or 'onClick' function when Link prop 'displayAsButton' is true.";
    }
  } catch (error) {
    throw new Error(error);
  }

  /*
    The following regex does the following:
      1. Matches if the url starts with `/pages/`
      2. Matches if the url starts with `http://www.lucidchart.com/pages`
        or `https://www.lucidchart.com/pages/`
        or `www.lucidchart.com/pages/`
        or `http://lucidchart.com/pages/`
        or `https://lucidchart.com/pages/`
      3. Matches if the url starts with `/`
        but doesn't start with `/blog`
        and `/documents`
        and `/users`
        and `/es/users/`
        (?:users?|blog|documents)
  */
  const blocklistRegex = mergeBlocklist
    ? `(?!(?:../|..-../)?(?:${mergeBlocklist.join('|')}))`
    : '';
  const pathPrefixRegex = pathPrefix ? `${pathPrefix}/|` : '';
  const hybridRegex = new RegExp(
    `^(?:(?://|https?://)?(?:www.)?${siteDomain}(?:${pathPrefixRegex}/$|$)/?|${pathPrefixRegex}/(?!/)${blocklistRegex})(.*)`
  );

  // render as button html if both asButton and onClick are not falsey
  // const renderAsButtonHTML = asButton && onClick !== undefined;
  const elementType = asButton ? 'button' : 'a';
  const internal = hrefTo && hybridRegex.exec(hrefTo!);

  const linkHasArrowStyles = (theme: ThemeProps['theme']) => css([
    underlineLinkOnHover && {
      '&:hover, &:focus': {
        textDecoration: underlineLinkOnHover
      }
    },
    {
      label: 'hasArrow',
      display: 'inline-block',
      fontSize: '0.65em',
      marginLeft: '0.5rem',
      transition: 'transform 0.15s ease-out',
      position: 'relative',
      color: isLucidchart
        ? theme.colors.indigo[60]
        : isLucidco
          ? theme.colors.blue[60]
          : isLucidedu
            ? theme.colors.indigo[70]
            : isLucidpress
              ? 'inherit'
              : isLucidspark
                ? theme.colors.red[60]
                : theme.colors.coolGrey[100]
    }
  ]);

  // Currently only used on case-study template breadcrumb links
  // to utillize the 'back' functionality, which will keep previous query
  //  params, rather than navigate back to the previous page loaded new
  // without query params.
  const beforeNavigation = (e: MouseEvent<HTMLAnchorElement>) => {
    if (checkHistory) {
      const prevLocation = sessionStorage.getItem('prevLocation');
      const baseTo = hrefTo.split('?')[0];
      if (baseTo === prevLocation) {
        e.preventDefault();
        window.history.back();
      }
    }
  };

  const isAnchorPath = (p: string) => p.startsWith('#');

  const onClickHandler = (e: MouseEvent<HTMLAnchorElement>) => {
    if (beaconEvent) {
      sendBeaconEvent(beaconEvent);
    }
    beforeNavigation(e);
    onClick?.(e);
    if (isAnchorPath(hrefTo)) {
      e.preventDefault();
      navigate(hrefTo);
    }
  };

  const newTabProps =
    newTab || externalAlwaysNewTab
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

  const noFollow = !internal &&
    (hrefTo?.includes('/documents/') ||
      (hrefTo?.includes('/users/') && hrefTo?.includes('?')) ||
      hrefTo?.match(/lucid?(chart|spark)/gi));

  const linkButtonCSS = displayAsButton || asButton ? buttonStyles : linkStyles;
  // Use Gatsby Link for internal links that open in the same tab, and <a> for others
  if (internal && !newTab && !excludeFromGatsby) {
    return (
      <StyledGatsbyLink
        {...rest}
        data-link-is-gatsby
        css={linkButtonCSS}
        /* eslint-disable-next-line react/jsx-sort-props */
        activeClassName={activeClassName}
        /* eslint-disable-next-line react/jsx-sort-props */
        className={className}
        data-link-as-button={asButton || displayAsButton ? true : undefined}
        data-link-as-link={!asButton && !displayAsButton ? true : undefined}
        onClick={onClickHandler}
        partiallyActive={partiallyActive}
        rel={`${noFollow ? 'nofollow ' : ''}${rel ? rel : ''}`.trim() || undefined}
        to={`/${internal[1]}`}
      >
        {children}
        {hasArrow && (!asButton && !displayAsButton) && (
          <span
            /* eslint-disable-next-line @emotion/syntax-preference */
            css={linkHasArrowStyles(theme)}
            /* eslint-disable-next-line react/jsx-sort-props */
            className="hasArrow"
          >
            <Icon type={IconType.ArrowRight} />
          </span>
        )}
      </StyledGatsbyLink>
    );
  }

  return (
    jsx(
      elementType,
      {
        ...rest,
        ...newTabProps,
        css: linkButtonCSS,
        className,
        disabled,
        'data-link-as-button': asButton || displayAsButton ? true : undefined,
        'data-link-as-link': !asButton && !displayAsButton ? true : undefined,
        href: disabled
          ? undefined
          : hrefTo
            ? internal && isLucidpress && !internal[0].includes('/pages')
              ? withPrefix(internal[0])
              : hrefTo
            : undefined,
        onClick: onClickHandler,
        type: type && asButton ? type : undefined
      },
      <React.Fragment>
        {children}
        {hasArrow && (!asButton && !displayAsButton) && (
          <span
            /* eslint-disable-next-line @emotion/syntax-preference */
            css={linkHasArrowStyles(theme)}
            /* eslint-disable-next-line react/jsx-sort-props */
            className="hasArrow"
          >
            <Icon type={IconType.ArrowRight} />
          </span>
        )}
      </React.Fragment>
    )
  );
};

const Link = withTheme(LinkBase);
export default Link;
